import React, { useState, useRef, useEffect, useCallback } from "react";
import { Breadcrumbs } from "../../../../AbstractElements";
import RoomDetails from "./RoomDetails";
import "./GroupRoom.scss";
import {
  getGroupsApi,
  getGroupByIdApi,
  createGroupApi,
  updateGroupApi,
  updateGroupStatusApi,
  addGroupMemberApi,
  removeGroupMemberApi,
  getDepartmentsApi,
  getUsersApi,
  deleteGroupApi,
  getPresignedUploadUrlApi,
} from "../../../../api/api";

// ─── Constants ───────────────────────────────────────────────────────────────
const API_STATUS_MAP = {
  ACTIVE: "Active",
  INACTIVE: "Inactive",
  ARCHIVED: "Archived",
};

const normaliseGroup = (g) => ({
  id: g.id,
  name: g.name || g.roomName,
  type: "Group",
  members: g.memberCount ?? g.members?.length ?? 0,
  createdOn: g.createdAt
    ? new Date(g.createdAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—",
  status: API_STATUS_MAP[g.status] || g.status || "Active",
  owner: g.createdBy?.fullName || g.owner || "Admin",
  department: g.department?.name || g.departmentId || "",
  departmentId: g.department?.id || g.departmentId || "",
  description: g.description || "",
  fileLimit: g.fileLimit ?? 50,
  messagePolicy: g.messagePolicy || "All members can post",
  retention: g.retention || "90 days",
  groupImage: g.groupImage || null,
  admins: (g.members || [])
    .filter((m) => m.memberRole === "ADMIN")
    .map((m) => m.fullName || m.userId),
  membersData: g.members || [],
});

// ─── Tiny Helpers ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    Active: { bg: "#D1FAE5", color: "#065F46", dot: "#10B981" },
    Inactive: { bg: "#FEF3C7", color: "#92400E", dot: "#F59E0B" },
    Archived: { bg: "#F3F4F6", color: "#6B7280", dot: "#9CA3AF" },
  };
  const s = map[status] || map.Archived;
  return (
    <span className="status-badge" style={{ background: s.bg, color: s.color }}>
      <span className="dot" style={{ background: s.dot }} />
      {status}
    </span>
  );
};

const Avatar = ({ name, size = 32 }) => {
  const initials = (name || "?")
    .split(" ")
    .slice(-2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  const hue = ((name || "?").charCodeAt(0) * 5) % 360;
  return (
    <span
      className="avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.35,
        background: `hsl(${hue},55%,35%)`,
      }}
    >
      {initials}
    </span>
  );
};

// ─── Confirm Modal ─────────────────────────────────────────────────────────────
const ConfirmModal = ({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  variant = "warn",
}) => {
  if (!open) return null;
  const isDanger = variant === "danger";
  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <div className="modal-warn-icon">{isDanger ? "🗑️" : "⚠️"}</div>
        <h4 className="modal-title">{title}</h4>
        <p className="modal-msg">{message}</p>
        <div className="modal-actions">
          <button className="btn-primary" onClick={onCancel}>
            Cancel
          </button>
          <button
            className={isDanger ? "btn-delete" : "btn-danger"}
            onClick={onConfirm}
            style={
              isDanger
                ? {
                    background: "#DC2626",
                    color: "#fff",
                    border: "none",
                    padding: "8px 20px",
                    borderRadius: 6,
                    fontWeight: 600,
                    cursor: "pointer",
                  }
                : {}
            }
          >
            {isDanger ? "Delete Permanently" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Create/Edit Drawer ────────────────────────────────────────────────────────
const RoomDrawer = ({
  open,
  mode,
  room,
  onClose,
  onSave,
  departments,
  allUsers,
}) => {
  const [form, setForm] = useState({
    name: "",
    department: "",
    departmentId: "",
    description: "",
    admins: [],
    adminIds: [],
    members: [],
    memberIds: [],
    messagePolicy: "All members can post",
    fileLimit: 50,
    retention: "90 days",
    groupImage: null,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setErrors({});
      setSaving(false);
      if (mode === "edit" && room) {
        const membersData = room.membersData || [];

        setForm({
          name: room.name || "",
          department: room.department || "",
          departmentId: room.departmentId || "",
          description: room.description || "",
          admins: room.admins || [],
          adminIds: membersData
            .filter((m) => m.memberRole === "ADMIN")
            .map((m) => m.userId),
          members: membersData
            .filter((m) => m.memberRole === "MEMBER")
            .map((m) => m.fullName),
          memberIds: membersData
            .filter((m) => m.memberRole === "MEMBER")
            .map((m) => m.userId),
          messagePolicy: room.messagePolicy || "All members can post",
          fileLimit: room.fileLimit ?? 50,
          retention: room.retention || "90 days",
          groupImage: room.groupImage || null,
        });
      } else {
        setForm({
          name: "",
          department: "",
          departmentId: "",
          description: "",
          admins: [],
          adminIds: [],
          members: [],
          memberIds: [],
          messagePolicy: "All members can post",
          fileLimit: 50,
          retention: "90 days",
          groupImage: null,
        });
      }
    }
  }, [open, mode, room]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Room name is required";
    return e;
  };

  const toggleUser = (field, idField, user) => {
    const inList = form[idField].includes(user.id);
    setForm({
      ...form,
      [field]: inList
        ? form[field].filter((n) => n !== user.name)
        : [...form[field], user.name],
      [idField]: inList
        ? form[idField].filter((i) => i !== user.id)
        : [...form[idField], user.id],
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const { data } = await getPresignedUploadUrlApi({
        fileName: file.name,
        mimeType: file.type,
        folder: "profile-images",
      });

      await fetch(data.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });

      setForm({ ...form, groupImage: data.fileUrl });
    } catch (err) {
      alert("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="room-drawer">
        <div className="drawer-header">
          <div>
            <h3 className="drawer-title">
              {mode === "edit" ? "Edit Group" : "Create Group"}
            </h3>
            <p className="drawer-subtitle">
              {mode === "edit"
                ? "Update group settings"
                : "Create a new group room"}
            </p>
          </div>
          <button className="drawer-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="drawer-body">
          <div className="form-section">
            {/* Group Image */}
            <div className="form-group">
              <label>Group Image</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {form.groupImage ? (
                  <img
                    src={form.groupImage}
                    alt=""
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: 8,
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: 8,
                      background: "#F3F4F6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 24,
                      color: "#9CA3AF",
                    }}
                  >
                    💬
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImageUpload}
                />
                <button
                  type="button"
                  className="btn-primary"
                  style={{ padding: "6px 14px", fontSize: 12 }}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading
                    ? "Uploading…"
                    : form.groupImage
                      ? "Change"
                      : "Upload"}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>
                Group Name <span className="req">*</span>
              </label>
              <input
                className={`form-input${errors.name ? " input-error" : ""}`}
                placeholder="e.g. Operations Team"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              {errors.name && <p className="error-msg">{errors.name}</p>}
            </div>
            <div className="form-group">
              <label>Department</label>
              <select
                className="form-input"
                value={form.departmentId}
                onChange={(e) => {
                  const dep = departments.find((d) => d.id === e.target.value);
                  setForm({
                    ...form,
                    departmentId: e.target.value,
                    department: dep?.name || "",
                  });
                }}
              >
                <option value="">None</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                className="form-input"
                rows={3}
                style={{
                  height: "auto",
                  padding: "10px 12px",
                  resize: "vertical",
                }}
                placeholder="Group purpose and usage..."
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label>Members</label>
              <p className="section-hint">Select admins and members.</p>
              <div className="form-group">
                <label style={{ fontSize: 12, color: "#6B7280" }}>Admins</label>
                <div className="multi-select-list">
                  {allUsers.map((u) => (
                    <label key={u.id} className="check-label">
                      <input
                        type="checkbox"
                        checked={form.adminIds.includes(u.id)}
                        onChange={() => toggleUser("admins", "adminIds", u)}
                      />
                      <Avatar name={u.name} size={24} />
                      <span>{u.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label style={{ fontSize: 12, color: "#6B7280" }}>
                  Members
                </label>
                <div className="multi-select-list">
                  {allUsers.map((u) => (
                    <label key={u.id} className="check-label">
                      <input
                        type="checkbox"
                        checked={form.memberIds.includes(u.id)}
                        onChange={() => toggleUser("members", "memberIds", u)}
                      />
                      <Avatar name={u.name} size={24} />
                      <span>{u.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="member-count-preview">
                <span>👥</span>
                <span>
                  <strong>
                    {form.adminIds.length + form.memberIds.length}
                  </strong>{" "}
                  members
                </span>
              </div>
            </div>

            <div className="form-group">
              <label>Message Posting Policy</label>
              <div className="radio-group">
                {["All members can post", "Admins only"].map((p) => (
                  <label key={p} className="radio-label">
                    <input
                      type="radio"
                      name="msgPolicy"
                      checked={form.messagePolicy === p}
                      onChange={() => setForm({ ...form, messagePolicy: p })}
                    />
                    {p}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="drawer-footer">
          <button className="btn-primary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={saving}
            onClick={() => {
              const e = validate();
              if (Object.keys(e).length) {
                setErrors(e);
                return;
              }
              onSave(form, setSaving);
            }}
          >
            {saving
              ? "Saving…"
              : mode === "edit"
                ? "Save Changes"
                : "Create Group"}
          </button>
        </div>
      </div>
    </>
  );
};

// ─── Main GroupRoom Component ─────────────────────────────────────────────────
export default function GroupRoom() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [departments, setDepartments] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [drawer, setDrawer] = useState({
    open: false,
    mode: "add",
    room: null,
  });
  const [selectedRoom, setSelectedRoom] = useState(null);

  const [confirm, setConfirm] = useState({ open: false, room: null });
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    room: null,
  });

  const [rowMenu, setRowMenu] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const rowMenuRef = useRef();

  useEffect(() => {
    const h = (e) => {
      if (rowMenuRef.current && !rowMenuRef.current.contains(e.target))
        setRowMenu(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const fetchGroups = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);
      try {
        const res = await getGroupsApi(page, pagination.limit);
        const data = res.data;
        setRooms((data.groups || []).map(normaliseGroup));
        if (data.pagination) setPagination(data.pagination);
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load rooms.");
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit],
  );

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await getDepartmentsApi();
      setDepartments(res.data?.departments || res.data || []);
    } catch {}
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await getUsersApi(1, 100);
      const raw = res.data?.users || res.data || [];
      setAllUsers(
        raw.map((u) => ({
          id: u.id,
          name: u.fullName || u.username || u.email,
        })),
      );
    } catch {}
  }, []);

  useEffect(() => {
    fetchGroups(1);
    fetchDepartments();
    fetchUsers();
  }, []);

  const filtered = rooms.filter((r) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      r.name.toLowerCase().includes(q) ||
      r.owner.toLowerCase().includes(q) ||
      r.department.toLowerCase().includes(q);
    const matchStatus = statusFilter === "All" || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalMembers = rooms.reduce((s, r) => s + r.members, 0);
  const activeRooms = rooms.filter((r) => r.status === "Active").length;

  const handleSave = async (form, setSaving) => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        departmentId: form.departmentId || undefined,
        roomType: "GROUP",
        messagePolicy: form.messagePolicy,
        retention: form.retention,
        fileLimit: form.fileLimit,
        groupImage: form.groupImage || undefined,
      };

      if (drawer.mode === "edit") {
        await updateGroupApi(drawer.room.id, payload);
        for (const userId of form.adminIds) {
          try {
            await addGroupMemberApi(drawer.room.id, {
              userId,
              memberRole: "ADMIN",
            });
          } catch {}
        }
        for (const userId of form.memberIds) {
          try {
            await addGroupMemberApi(drawer.room.id, {
              userId,
              memberRole: "MEMBER",
            });
          } catch {}
        }
      } else {
        const res = await createGroupApi(payload);
        const newGroupId = res.data?.group?.id;
        if (newGroupId) {
          for (const userId of form.adminIds) {
            try {
              await addGroupMemberApi(newGroupId, {
                userId,
                memberRole: "ADMIN",
              });
            } catch {}
          }
          for (const userId of form.memberIds) {
            try {
              await addGroupMemberApi(newGroupId, {
                userId,
                memberRole: "MEMBER",
              });
            } catch {}
          }
        }
      }

      setDrawer({ open: false, mode: "add", room: null });
      fetchGroups(pagination.page);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to save room.");
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    const r = confirm.room;
    setActionLoading(true);
    try {
      await updateGroupStatusApi(r.id, { status: "ARCHIVED" });
      setRooms((prev) =>
        prev.map((x) => (x.id === r.id ? { ...x, status: "Archived" } : x)),
      );
      if (selectedRoom?.id === r.id)
        setSelectedRoom((prev) => ({ ...prev, status: "Archived" }));
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to archive room.");
    } finally {
      setActionLoading(false);
      setConfirm({ open: false, room: null });
    }
  };

  const handleDelete = async () => {
    const r = deleteConfirm.room;
    setActionLoading(true);
    try {
      await deleteGroupApi(r.id);
      setRooms((prev) => prev.filter((x) => x.id !== r.id));
      if (selectedRoom?.id === r.id) setSelectedRoom(null);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete room.");
    } finally {
      setActionLoading(false);
      setDeleteConfirm({ open: false, room: null });
    }
  };

  const handleViewRoom = (room) => {
    setSelectedRoom(room);
    getGroupByIdApi(room.id)
      .then((res) => {
        const fullRoom = normaliseGroup(res.data?.group || res.data);
        setSelectedRoom(fullRoom);
        // Also update the room in the rooms array so row click has full data
        setRooms((prev) => prev.map((r) => (r.id === room.id ? fullRoom : r)));
      })
      .catch(() => {});
  };

  if (selectedRoom) {
    return (
      <div className="gr-page">
        <Breadcrumbs
          parent="Communication"
          title={selectedRoom.name}
          mainTitle="Groups"
        />
        <RoomDetails
          room={selectedRoom}
          onBack={() => setSelectedRoom(null)}
          onEdit={(r) => {
            setSelectedRoom(null);
            setDrawer({ open: true, mode: "edit", room: r });
          }}
          onArchive={(r) => setConfirm({ open: true, room: r })}
          onDelete={(r) => setDeleteConfirm({ open: true, room: r })}
        />
        <RoomDrawer
          open={drawer.open}
          mode={drawer.mode}
          room={drawer.room}
          departments={departments}
          allUsers={allUsers}
          onClose={() => setDrawer({ open: false, mode: "add", room: null })}
          onSave={handleSave}
        />
        <ConfirmModal
          open={confirm.open}
          title="Archive Room?"
          message={`Archiving "${confirm.room?.name}" will disable all activity.`}
          onConfirm={handleArchive}
          onCancel={() => setConfirm({ open: false, room: null })}
          variant="warn"
        />
        <ConfirmModal
          open={deleteConfirm.open}
          title="Delete Room Permanently?"
          message={`Deleting "${deleteConfirm.room?.name}" will permanently remove the room, all messages, and member data.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteConfirm({ open: false, room: null })}
          variant="danger"
        />
      </div>
    );
  }

  return (
    <div className="gr-page">
      <Breadcrumbs parent="Communication" title="Groups" mainTitle="Groups" />

      <div className="kpi-row">
        {[
          {
            label: "Total Groups",
            value: pagination.total || rooms.length,
            icon: "💬",
            color: "#0062FE",
          },
          { label: "Active", value: activeRooms, icon: "✅", color: "#059669" },
          {
            label: "Total Members",
            value: totalMembers.toLocaleString(),
            icon: "👥",
            color: "#7C3AED",
          },
        ].map((k) => (
          <div className="kpi-card" key={k.label}>
            <span className="kpi-icon">{k.icon}</span>
            <div>
              <p className="kpi-value" style={{ color: k.color }}>
                {k.value}
              </p>
              <p className="kpi-label">{k.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="main-card">
        <div className="toolbar-group">
          <div className="toolbar-left">
            <div className="search-wrap">
              <svg
                className="search-icon"
                width="15"
                height="15"
                fill="none"
                viewBox="0 0 16 16"
              >
                <circle
                  cx="7"
                  cy="7"
                  r="5"
                  stroke="#9CA3AF"
                  strokeWidth="1.4"
                />
                <path
                  d="M11 11l3 3"
                  stroke="#9CA3AF"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
              <input
                className="search-input"
                placeholder="Search groups…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="filter-chips">
              {["All", "Active", "Archived"].map((f) => (
                <button
                  key={f}
                  className={`chip-group${statusFilter === f ? " chip-active" : ""}`}
                  onClick={() => setStatusFilter(f)}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="toolbar-right">
            <button
              className="btn-primary"
              onClick={() => fetchGroups(pagination.page)}
            >
              ↺ Refresh
            </button>
            <button
              className="btn-primary"
              onClick={() => setDrawer({ open: true, mode: "add", room: null })}
            >
              + Create Group
            </button>
          </div>
        </div>

        {error && (
          <div
            style={{
              padding: "12px 20px",
              background: "#FEE2E2",
              color: "#991B1B",
              fontSize: 13,
            }}
          >
            ⚠️ {error}{" "}
            <button
              style={{
                textDecoration: "underline",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#991B1B",
              }}
              onClick={() => fetchGroups(1)}
            >
              Retry
            </button>
          </div>
        )}

        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Group Name</th>
                <th>Members</th>
                <th>Department</th>
                <th>Owner</th>
                <th>Created On</th>
                <th>Status</th>
                <th style={{ width: 48 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      textAlign: "center",
                      padding: 48,
                      color: "#9CA3AF",
                    }}
                  >
                    Loading groups…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      textAlign: "center",
                      padding: 48,
                      color: "#9CA3AF",
                    }}
                  >
                    No groups found
                  </td>
                </tr>
              ) : (
                filtered.map((room) => (
                  <tr
                    key={room.id}
                    className="clickable-row"
                    onClick={() => handleViewRoom(room)}
                  >
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        {room.groupImage ? (
                          <img
                            src={room.groupImage}
                            alt=""
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 8,
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <span
                            className="room-icon-cell"
                            style={{ background: "#EFF6FF", color: "#0062FE" }}
                          >
                            💬
                          </span>
                        )}
                        <span className="room-name-cell">{room.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className="member-count">{room.members}</span>
                    </td>
                    <td style={{ fontSize: 13, color: "#374151" }}>
                      {room.department || "—"}
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                        }}
                      >
                        <Avatar name={room.owner} size={24} />
                        <span style={{ fontSize: 13 }}>{room.owner}</span>
                      </div>
                    </td>
                    <td style={{ fontSize: 13, color: "#6B7280" }}>
                      {room.createdOn}
                    </td>
                    <td>
                      <StatusBadge status={room.status} />
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div
                        style={{ position: "relative" }}
                        ref={rowMenu === room.id ? rowMenuRef : null}
                      >
                        <button
                          className="row-menu-btn"
                          onClick={() =>
                            setRowMenu(rowMenu === room.id ? null : room.id)
                          }
                        >
                          ⋮
                        </button>
                        {rowMenu === room.id && (
                          <div className="row-dropdown">
                            <button
                              onClick={() => {
                                handleViewRoom(room);
                                setRowMenu(null);
                              }}
                            >
                              👁 View Details
                            </button>

                            <button
                              onClick={async () => {
                                setRowMenu(null);
                                try {
                                  const res = await getGroupByIdApi(room.id);
                                  const fullRoom = normaliseGroup(
                                    res.data?.group || res.data,
                                  );
                                  setDrawer({
                                    open: true,
                                    mode: "edit",
                                    room: fullRoom,
                                  });
                                } catch {
                                  setDrawer({ open: true, mode: "edit", room });
                                }
                              }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              className="menu-danger"
                              onClick={() => {
                                setConfirm({ open: true, room });
                                setRowMenu(null);
                              }}
                              disabled={room.status === "Archived"}
                            >
                              🗄 Archive
                            </button>
                            <button
                              className="menu-danger"
                              style={{ color: "#DC2626" }}
                              onClick={() => {
                                setDeleteConfirm({ open: true, room });
                                setRowMenu(null);
                              }}
                            >
                              🗑 Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <span className="pag-label">
            {filtered.length} of {pagination.total || rooms.length} groups
          </span>
          <div style={{ display: "flex", gap: 4 }}>
            {Array.from(
              { length: pagination.totalPages || 1 },
              (_, i) => i + 1,
            ).map((p) => (
              <button
                key={p}
                className={`pag-btn${p === pagination.page ? " pag-active" : ""}`}
                onClick={() => fetchGroups(p)}
              >
                {p}
              </button>
            ))}
          </div>
          <select
            className="pag-select"
            value={pagination.limit}
            onChange={(e) => {
              setPagination((prev) => ({
                ...prev,
                limit: Number(e.target.value),
                page: 1,
              }));
              fetchGroups(1);
            }}
          >
            {[10, 25, 50].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <RoomDrawer
        open={drawer.open}
        mode={drawer.mode}
        room={drawer.room}
        departments={departments}
        allUsers={allUsers}
        onClose={() => setDrawer({ open: false, mode: "add", room: null })}
        onSave={handleSave}
      />
      <ConfirmModal
        open={confirm.open}
        title="Archive Group?"
        message={`Archiving "${confirm.room?.name}" will disable all activity.`}
        onConfirm={handleArchive}
        onCancel={() => setConfirm({ open: false, room: null })}
        variant="warn"
      />
      <ConfirmModal
        open={deleteConfirm.open}
        title="Delete Group Permanently?"
        message={`Deleting "${deleteConfirm.room?.name}" will permanently remove the group, all messages, and member data.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ open: false, room: null })}
        variant="danger"
      />
    </div>
  );
}
